import type { ComponentPropsWithRef, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { type LinkProps, parsePath, type To, useSearchParams } from 'react-router';

import { SearchParamKeys } from '~/constants/url';
import { clsx } from '~/styles/utils';

import { LinkButton } from '../Button/LinkButton';
import { Text } from '../Text/Text';
import { Title } from '../Title/Title';
import { contentStyle, mobileOnlyBackLinkStyle, wrapperStyle } from './HeaderContent.css';

type Props = Omit<ComponentPropsWithRef<'div'>, 'title'> & {
  backTo?: LinkProps['to'];
  backToMobileOnly?: boolean;
  text?: string;
  title: ReactNode;
};

export const HeaderContent = ({ backTo, backToMobileOnly, text, title, ...delegated }: Props) => {
  const { t } = useTranslation(['common']);

  const redirectTo = useRedirectPath();
  const to = redirectTo ?? backTo;

  return (
    <div {...delegated} className={clsx(contentStyle, delegated.className)}>
      {to && (
        <LinkButton
          aria-label={t('common:actions.back')}
          // An inbound redirect is the user's only way back to where they came from, so it stays visible on every viewport.
          className={backToMobileOnly && !redirectTo ? mobileOnlyBackLinkStyle : undefined}
          icon="ArrowLeft"
          to={to}
        />
      )}

      <div className={wrapperStyle}>
        <Title as="h1">{title}</Title>

        {text && <Text>{text}</Text>}
      </div>
    </div>
  );
};

const useRedirectPath = (): To | null => {
  const [searchParams] = useSearchParams();

  const redirectPath = searchParams.get(SearchParamKeys.REDIRECT_PATH);

  if (!redirectPath) return null;

  const parsedRedirectPath = parsePath(redirectPath);
  const redirectSearchParams = new URLSearchParams(parsedRedirectPath.search);
  const redirectModal = searchParams.get(SearchParamKeys.REDIRECT_MODAL);

  if (redirectModal) redirectSearchParams.set(SearchParamKeys.OPEN_MODAL, redirectModal);

  return { ...parsedRedirectPath, search: redirectSearchParams.toString() };
};
